import { useEffect, useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import { api } from "../api/client";
import type { KnowledgeFile, Plan } from "../types";
import FileList from "../components/knowledge/FileList";
import UploadZone from "../components/knowledge/UploadZone";
import GenerationLoader from "../components/GenerationLoader";

export default function KnowledgePage() {
  const [files, setFiles] = useState<KnowledgeFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<Plan | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchFiles = async () => {
    try {
      const data = await api.getKnowledge();
      setFiles(data);
      return data;
    } catch (err) {
      console.error("Knowledge fetch error:", err);
      return [];
    }
  };

  // Poll every 3s while any file is pending
  useEffect(() => {
    const hasPending = files.some((f) => f.status === "pending");
    if (hasPending && !pollRef.current) {
      pollRef.current = setInterval(() => {
        void fetchFiles().then((data) => {
          if (!data.some((f) => f.status === "pending") && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        });
      }, 3000);
    } else if (!hasPending && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [files]);

  useEffect(() => {
    api
      .getUsage()
      .then((u) => {
        setPlan(u.plan);
        // The library is Maestro-only; don't load the shelf for lower tiers.
        if (u.plan === "maestro") return fetchFiles();
        return undefined;
      })
      .catch(() => { /* assume non-maestro */ })
      .finally(() => setLoading(false));
  }, []);

  const canUpload = plan === "maestro";

  const handleUpload = async (file: File) => {
    await api.uploadKnowledge(file);
    await fetchFiles();
  };

  const handleAddYouTube = async (url: string) => {
    await api.addYouTube(url);
    await fetchFiles();
  };

  const handleDelete = async (filename: string) => {
    await api.deleteKnowledge(filename);
    await fetchFiles();
  };

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <GenerationLoader
        messages={["Loading your library...", "Fetching Elena's reading list..."]}
        subtitle=""
      />
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-2xl mx-auto w-full px-5 sm:px-8 py-10">
        <header className="mb-8 animate-rise">
          <h1>Library</h1>
          <p className="text-text-tertiary text-[0.9375rem] mt-1">
            Notes, articles, and videos Elena has read — she brings them into conversation.
          </p>
        </header>
        {canUpload ? (
          <>
            <FileList files={files} onDelete={(f) => void handleDelete(f)} />
            <UploadZone onUpload={handleUpload} onAddUrl={handleAddYouTube} atLimit={files.length >= 3} />
          </>
        ) : (
          <div className="mt-6 rounded-xl border border-border-soft bg-surface2/40 px-6 py-10 text-center">
            <p className="text-text-secondary text-sm">
              The Library is a <span className="font-semibold text-success">Maestro</span> feature.
              Upload your own notes, articles, and YouTube videos for Elena to read and weave into
              your conversations.
            </p>
            <NavLink
              to="/upgrade"
              className="mt-4 inline-block text-success underline text-sm"
            >
              See plans
            </NavLink>
          </div>
        )}
      </div>
    </div>
  );
}
